import React, { useEffect, useMemo, useState } from "react"
import apiClient from "../../Utils/apiClient"
import PanelLayout from "../../Layout/PanelLayout"
import TableBox from "../../components/TableBox/TableBox"
import { DeleteOutlined, ReloadOutlined, UndoOutlined } from "@ant-design/icons"
import { Button, Card, DatePicker, Empty, message, Modal } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import AutoCompleteBox from "../../components/AutoCompleteBox/AutoCompleteBox"
import DeviceModelImage from "../../components/DeviceModelImage/DeviceModelImage"
import HostnameLink from "../../components/HostnameLink/HostnameLink"
import StatusTag from "../../components/StatusTag/StatusTag"
import { getStoredPageSize } from "../../Utils/TableUtils/TableUtils"
import { renderDate } from "../../Utils/utils"
import { resolveDeviceModelImage } from "../../Utils/deviceModelImage"
import "../Operations/Operations.scss"

dayjs.extend(relativeTime)

const AGE_VIEWS = [
  { key: "all", label: "All deleted", tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
  { key: "24h", label: "Last 24 hours", tone: "#dc2626", soft: "rgba(248, 113, 113, 0.14)" },
  { key: "7d", label: "Last 7 days", tone: "#ea580c", soft: "rgba(234, 88, 12, 0.16)" },
  { key: "older", label: "Older than 30 days", tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
]

const inAgeView = (row, key) => {
  if (key === "all") return true
  const at = dayjs(row?.deletedAt)
  if (!at.isValid()) return false
  if (key === "24h") return at.isAfter(dayjs().subtract(24, "hour"))
  if (key === "7d") return at.isAfter(dayjs().subtract(7, "day"))
  if (key === "older") return at.isBefore(dayjs().subtract(30, "day"))
  return true
}

const DeletedDevices = () => {
  const page = "Deleted Devices"
  const { RangePicker } = DatePicker
  const [deletedDevices, setDeletedDevices] = useState([])
  const [loading, setLoading] = useState(false)
  const [render, setRender] = useState(false)
  const [error, setError] = useState("")
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [exportData, setExportData] = useState([])
  const [ageView, setAgeView] = useState("all")
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  }

  const [filters, setFilters] = useState({
    hostname: "",
    user: "",
    dateRange: [],
  })

  const getDeletedDevices = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        requiresReboot: true,
        ...filters,
      }
      const res = await apiClient("/devices/deletedDevices", "POST", params)
      setDeletedDevices(res)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilter = () => {
    setFilters({
      hostname: "",
      user: "",
      dateRange: [],
    })
    getDeletedDevices(1, paginationData.pageSize)
    setRender((pre) => !pre)
  }
  useEffect(() => {
    getDeletedDevices()
  }, [render])

  const payload = deletedDevices?.devices
    ?.filter((device) => selectedRowKeys.includes(device._id))
    .map((device) => ({
      ids: device._id,
    }))

  const handleRestore = async () => {
    setLoading(true)
    setError("")
    try {
      const res = await apiClient("/devices/restore_device", "POST", payload)
      if (res.success) {
        getDeletedDevices()
        message.success("Devices restored successfully")
      } else {
        setError(res.message || "Failed to restore devices")
        message.error(res.message || "Failed to restore devices")
      }
    } catch (err) {
      setError(err)
      message.error(err.message || "An error occurred while restoring devices")
    } finally {
      setLoading(false)
    }
  }

  const showDeleteConfirm = () => {
    Modal.confirm({
      title: "Are you sure you want to permanently delete these devices?",
      icon: <DeleteOutlined style={{ color: "red" }} />,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No, Cancel",
      onOk: handlePermanentDelete,
    })
  }

  const handlePermanentDelete = async () => {
    try {
      const res = await apiClient("/devices/permanent_delete", "POST", payload)
      if (res.success) {
        getDeletedDevices()
        message.success("Devices permanently deleted successfully")
      } else {
        setError(res.message || "Failed to permanently delete devices")
        message.error(res.message || "Failed to permanently delete devices")
      }
    } catch (error) {
      console.error("Error during permanent delete:", error)
      message.error("An error occurred while deleting devices")
    }
  }

  const allRows = useMemo(
    () => deletedDevices?.devices || [],
    [deletedDevices]
  )

  const ageSummary = useMemo(() => {
    const summary = {}
    AGE_VIEWS.forEach((view) => {
      summary[view.key] = allRows.filter((row) => inAgeView(row, view.key)).length
    })
    return summary
  }, [allRows])

  const visibleRows = useMemo(
    () => allRows.filter((row) => inAgeView(row, ageView)),
    [allRows, ageView]
  )

  const columns = [
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 280,
      render: (text, record) => {
        const model = resolveDeviceModelImage(record)
        return (
          <div className="inv-device">
            <DeviceModelImage device={record} size="sm" />
            <div className="inv-device__copy">
              <div className="inv-device__title">
                <span
                  className="inv-device__pulse inv-device__pulse--deleted"
                  aria-hidden
                />
                <HostnameLink record={record} isAdmin={false}>
                  {text || "N/A"}
                </HostnameLink>
              </div>
              <div className="inv-device__meta" title={record.user || ""}>
                <span>{record.user || "No user"}</span>
                <span className="inv-device__dot" />
                <span title={model.displayModel}>{model.label}</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "Group",
      dataIndex: "groupedIn",
      key: "groupedIn",
      render: (text) => text || <span className="ops-muted">—</span>,
    },
    {
      title: "State",
      key: "state",
      width: 110,
      render: () => <StatusTag status="Deleted" color="red" />,
    },
    {
      title: "Deleted",
      dataIndex: "deletedAt",
      key: "deletedAt",
      width: 170,
      render: (text) =>
        text ? (
          <div className="inv-device__copy">
            <div>{new Date(text).toLocaleDateString()}</div>
            <div className="inv-device__meta">
              <span>
                {new Date(text).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
                {" · "}
                {dayjs(text).fromNow()}
              </span>
            </div>
          </div>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Deleted By",
      key: "deletedBy",
      render: (_, row) =>
        row.deletedByEmail || row.deletedByName || "Unknown",
    },
  ]

  const formatExportData = (data) => {
    return data?.map((device) => {
      const disks = device?.sysInfo?.Disks || []
      const totalDiskSize = disks.reduce(
        (sum, disk) => sum + (parseFloat(disk["Size (GB)"]) || 0),
        0
      )

      return {
        hostname: device?.hostname,
        user: device?.user,
        reboot: device?.reboot,
        manufacturer: device?.sysInfo?.Manufacturer,
        model: device?.sysInfo?.Model,
        systemType: device?.sysInfo?.["System Type"],
        totalMemory: device?.sysInfo?.["Total Physical Memory (GB)"],
        totalDiskSize: totalDiskSize.toFixed(2),
        serialNumber: device?.sysInfo?.SerialNumber,
        usbStatus: device?.usbStatus,
        deletedAt: renderDate(device?.deletedAt),
        deletedBy: device?.deletedByEmail || device?.deletedByName || "Unknown",
      }
    })
  }

  const exportOptions = {
    endpoint: "filter/deleted-devices",
    method: "POST",
    params: {
      ...filters,
    },
    onExportData: (data) => setExportData(data),
    format: formatExportData,
    data: formatExportData(exportData),
    header: [
      "Hostname",
      "User",
      "Last Reboot",
      "Manufacturer",
      "Model",
      "System Type",
      "Total Memory (GB)",
      "Total Disk Size (GB)",
      "Serial Number",
      "USB Status",
      "Deleted At",
      "Deleted By",
    ],
    btnTitle: "Export",
    fileName: "Deleted Devices",
    pdfWithout: ["systemType", "reboot"],
  }

  return (
    <PanelLayout title="Manage Deleted Devices"
      subtitle="Restore or permanently remove devices that were soft-deleted.">
      <div className="ops-kpis">
        {AGE_VIEWS.map((card) => (
          <button
            type="button"
            key={card.key}
            className={`ops-kpi${ageView === card.key ? " is-active" : ""}`}
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
            onClick={() => setAgeView(card.key)}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong>{ageSummary[card.key] ?? 0}</strong>
            </div>
          </button>
        ))}
      </div>

      <div className="ops-toolbar">
        <span className="ops-toolbar__count">
          {visibleRows.length} device{visibleRows.length === 1 ? "" : "s"}
          {ageView !== "all"
            ? ` · ${AGE_VIEWS.find((c) => c.key === ageView)?.label}`
            : ""}
          {selectedRowKeys.length ? ` · ${selectedRowKeys.length} selected` : ""}
        </span>
        <div className="dashboard-filter-chips">
          <span>Deleted</span>
          {AGE_VIEWS.map((chip) => (
            <Button
              key={chip.key}
              size="small"
              type={ageView === chip.key ? "primary" : "default"}
              onClick={() => setAgeView(chip.key)}
            >
              {chip.key === "all" ? "All" : chip.label.replace("Last ", "")}
            </Button>
          ))}
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => getDeletedDevices()}
        >
          Refresh
        </Button>
        {selectedRowKeys.length > 0 && (
          <>
            <Button
              type="primary"
              icon={<UndoOutlined />}
              onClick={handleRestore}
              disabled={loading}
            >
              Restore ({selectedRowKeys.length})
            </Button>
            <Button
              danger
              type="primary"
              icon={<DeleteOutlined />}
              onClick={showDeleteConfirm}
              disabled={loading}
            >
              Delete ({selectedRowKeys.length})
            </Button>
          </>
        )}
      </div>

      <div className="d-flex flex-column gap-2">
        <div>
          {deletedDevices?.devices?.length === 0 ? (
            <Card>
              <Empty description="No Deleted Devices Found" />
            </Card>
          ) : (
            <TableBox
              title="Deleted devices"
              page={`Patch Updates`}
              className="dashboard-inventory-table"
              data={visibleRows}
              columns={columns}
              loading={loading}
              rowSelection={rowSelection}
              enableFilter
              filtercontent={
                <div className="p-3">
                  <AutoCompleteBox
                    label="Hostname"
                    name="hostname"
                    className="mb-3 w-100"
                    endPoint="deleted-devices"
                    placeholder="Search Hostname"
                    value={filters.hostname}
                    onChange={(value) =>
                      setFilters((prev) => ({ ...prev, hostname: value }))
                    }
                  />
                  <AutoCompleteBox
                    label="User"
                    name="user"
                    className="mb-3 w-100"
                    endPoint="deleted-devices"
                    placeholder="Search Title"
                    value={filters.user}
                    onChange={(value) =>
                      setFilters((prev) => ({ ...prev, user: value }))
                    }
                  />

                  <div className="mb-3">
                    <label className="form-label d-block">
                      Start & End Date
                    </label>
                    <RangePicker
                      className="w-100"
                      value={filters.dateRange}
                      onChange={(dates) =>
                        setFilters((prev) => ({
                          ...prev,
                          dateRange: dates || [],
                        }))
                      }
                    />
                  </div>
                </div>
              }
              onFilter={() => {
                setPaginationData((prev) => ({ ...prev, page: 1 }))
                getDeletedDevices(1, paginationData.pageSize, filters)
              }}
              onClear={handleClearFilter}
              filters={filters}
              exportable
              exportOptions={exportOptions}
            />
          )}
        </div>
      </div>
    </PanelLayout>
  )
}

export default DeletedDevices
